import React, { useState, useRef, useEffect, useMemo } from 'react';
import { getCountries, getCountryCallingCode, parsePhoneNumber, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { ChevronDown, Search, X } from 'lucide-react';

// Convert Arabic-Indic (٠-٩, U+0660–U+0669) and Persian / Eastern
// Arabic-Indic (۰-۹, U+06F0–U+06F9) digits to their Western (0-9)
// equivalents BEFORE any regex stripping.
//
// Why this exists (Jun 2026 — root cause for the رندا/Safari-iPhone
// registration failure):
//   - iOS Safari with an Arabic system keyboard inserts Arabic-Indic
//     digits even when `<input type="tel" inputMode="numeric" />` is
//     used. The OS-level keyboard layout wins over the input-mode hint.
//   - The old code did `rawText.replace(/[^\d]/g, '')`, but `\d` in
//     ECMAScript matches ONLY `[0-9]`. Arabic-Indic and Persian digits
//     were silently stripped, leaving an empty number → registration
//     blocked, no obvious error → user gives up.
//   - Normalising at the entry point makes every downstream step
//     (libphonenumber-js parsing, E.164 formatting, validation) work
//     regardless of which numeric script the user typed.
export const normaliseDigits = (s) => {
  if (!s) return '';
  let out = '';
  for (const ch of String(s)) {
    const code = ch.codePointAt(0);
    if (code >= 0x0660 && code <= 0x0669) {
      // Arabic-Indic ٠-٩
      out += String.fromCharCode(0x30 + (code - 0x0660));
    } else if (code >= 0x06F0 && code <= 0x06F9) {
      // Eastern Arabic-Indic / Persian ۰-۹
      out += String.fromCharCode(0x30 + (code - 0x06F0));
    } else {
      out += ch;
    }
  }
  return out;
};

// Minimal country name table (English). Falls back to ISO code.
// Curated to keep bundle small; arbitrary country can still be picked from the
// list because we use libphonenumber-js's getCountries() as the source of truth.
const COUNTRY_NAMES = {
  SA: 'Saudi Arabia', YE: 'Yemen', AE: 'United Arab Emirates', EG: 'Egypt',
  JO: 'Jordan', KW: 'Kuwait', QA: 'Qatar', BH: 'Bahrain', OM: 'Oman',
  LB: 'Lebanon', SY: 'Syria', IQ: 'Iraq', PS: 'Palestine', SD: 'Sudan',
  MA: 'Morocco', TN: 'Tunisia', DZ: 'Algeria', LY: 'Libya', US: 'United States',
  GB: 'United Kingdom', CA: 'Canada', AU: 'Australia', IN: 'India',
  PK: 'Pakistan', BD: 'Bangladesh', ID: 'Indonesia', MY: 'Malaysia',
  TR: 'Turkey', DE: 'Germany', FR: 'France', IT: 'Italy', ES: 'Spain',
  NL: 'Netherlands', BE: 'Belgium', CH: 'Switzerland', AT: 'Austria',
  SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland', RU: 'Russia',
  UA: 'Ukraine', PL: 'Poland', RO: 'Romania', CZ: 'Czechia', HU: 'Hungary',
  GR: 'Greece', PT: 'Portugal', IE: 'Ireland', JP: 'Japan', KR: 'South Korea',
  CN: 'China', SG: 'Singapore', PH: 'Philippines', TH: 'Thailand', VN: 'Vietnam',
  ZA: 'South Africa', NG: 'Nigeria', KE: 'Kenya', ET: 'Ethiopia', GH: 'Ghana',
  BR: 'Brazil', MX: 'Mexico', AR: 'Argentina', CL: 'Chile', CO: 'Colombia',
  PE: 'Peru', VE: 'Venezuela', NZ: 'New Zealand', IR: 'Iran', AF: 'Afghanistan',
};

const flagEmoji = (iso2) => {
  // Convert ISO 3166-1 alpha-2 to regional indicator emoji.
  if (!iso2 || iso2.length !== 2) return '🏳️';
  const base = 0x1f1e6;
  return String.fromCodePoint(base + iso2.charCodeAt(0) - 65) +
         String.fromCodePoint(base + iso2.charCodeAt(1) - 65);
};

/**
 * PhoneInput — country selector + national-number input.
 *
 * Props:
 *  - value: current E.164 string (e.g. "+966512345678") OR empty
 *  - onChange: (e164OrNull) => void — null when invalid/empty
 *  - defaultCountry: ISO-3166-1 alpha-2 (default "SA")
 *  - placeholder: optional national-number placeholder
 *  - testId: data-testid prefix
 *  - disabled: bool
 */
export const PhoneInput = ({
  value = '',
  onChange,
  defaultCountry = 'SA',
  placeholder = '5XXXXXXXX',
  testId = 'phone-input',
  disabled = false,
  invalid = false,
  variant = 'light',
}) => {
  const [country, setCountry] = useState(defaultCountry);
  const [nationalNumber, setNationalNumber] = useState('');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // 2026-06-29 — silent-failure regression (roudah bug): when the
  // typed digits don't yet form a valid number for the chosen
  // country, we previously emitted `onChange(null)` with no UI cue
  // and the parent form happily submitted with WhatsApp=null. We now
  // track an internal "user has typed something but it's still
  // invalid" flag and surface a red border + inline error message
  // so the user sees the problem before submitting.
  const [internalInvalid, setInternalInvalid] = useState(false);
  // Stable per-instance random suffix used to confuse Chrome / Samsung
  // Internet password-manager autofill into ignoring this phone input. Must
  // be generated once and persisted; recomputing it on every render would
  // break focus + cursor position while typing.
  const [autofillBreaker] = useState(() => Math.random().toString(36).slice(2, 8));
  const wrapperRef = useRef(null);

  // Hydrate from value when it changes externally. NEVER clear our local
  // ``nationalNumber`` just because the parent value transitioned back to ''.
  // That used to happen during typing whenever an intermediate-length input was
  // not yet valid — and it wiped what the user just typed for SA / YE / etc.
  useEffect(() => {
    if (!value) return;
    try {
      const parsed = parsePhoneNumber(value);
      if (parsed && parsed.country) {
        setCountry(parsed.country);
        setNationalNumber(parsed.nationalNumber);
      }
    } catch {
      // value is not parseable; leave national number alone
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const allCountries = useMemo(() => {
    const items = getCountries().map((iso) => ({
      iso,
      name: COUNTRY_NAMES[iso] || iso,
      dialCode: '+' + getCountryCallingCode(iso),
    }));
    items.sort((a, b) => a.name.localeCompare(b.name));
    return items;
  }, []);

  const filteredCountries = useMemo(() => {
    if (!search) return allCountries;
    const q = search.toLowerCase();
    return allCountries.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.iso.toLowerCase().includes(q) ||
      c.dialCode.includes(q),
    );
  }, [allCountries, search]);

  const dialCode = useMemo(() => {
    try { return '+' + getCountryCallingCode(country); } catch { return '+'; }
  }, [country]);

  const propagate = (iso, national) => {
    const cleaned = (national || '').replace(/[^\d]/g, '');
    if (!cleaned) {
      setInternalInvalid(false);   // empty is "not invalid", just empty
      onChange?.(null);
      return;
    }
    // Phase 7.5 fix: use country-aware parser so leading-zero domestic formats
    // (e.g. SA ``05XXXXXXXX``, YE ``0771234567``) are normalized to E.164.
    // Falling back to "+<dial> + national" loses validity for those forms.
    try {
      const parsed = parsePhoneNumberFromString(cleaned, iso);
      if (parsed && parsed.isValid()) {
        setInternalInvalid(false);
        onChange?.(parsed.format('E.164'));
        return;
      }
    } catch {
      // fall through to legacy concat check
    }
    // Legacy concat path — only emit when the strict isValidPhoneNumber agrees.
    const e164Candidate = '+' + getCountryCallingCode(iso) + cleaned;
    if (isValidPhoneNumber(e164Candidate, iso)) {
      setInternalInvalid(false);
      onChange?.(e164Candidate);
    } else {
      setInternalInvalid(true);    // user typed something but it's not valid
      onChange?.(null);
    }
  };

  const handleSelect = (iso) => {
    setCountry(iso);
    setOpen(false);
    setSearch('');
    propagate(iso, nationalNumber);
  };

  const handleInput = (e) => {
    // Jun 2026 — normalise non-Western digits FIRST so Safari/iOS
    // Arabic-keyboard input (٠١٢٣٤٥٦٧٨٩) and Persian-keyboard input
    // (۰۱۲۳۴۵۶۷۸۹) are treated identically to Western input. Without
    // this, `replace(/[^\d]/g, '')` below stripped every character →
    // empty number → silent registration failure (the رندا bug,
    // Jun 22 2026).
    const rawText = normaliseDigits(e.target.value);
    // ── Feb 2026 fix ──────────────────────────────────────────────────
    // If the user pastes (or types) an international prefix — either
    // "+967711963181" or "00967711963181" — auto-detect the country and
    // re-route. Without this, a Yemeni number pasted into the SA-defaulted
    // input was silently rejected (digits stripped of "+", then validated
    // against SA → invalid → onChange(null)).
    const trimmed = rawText.trim();
    let intl = null;
    if (trimmed.startsWith('+')) {
      intl = trimmed;
    } else if (trimmed.startsWith('00') && trimmed.length > 3) {
      intl = '+' + trimmed.slice(2);
    }
    if (intl) {
      try {
        const parsed = parsePhoneNumberFromString(intl);
        if (parsed && parsed.country) {
          setCountry(parsed.country);
          setNationalNumber(parsed.nationalNumber || '');
          const ok = parsed.isValid();
          setInternalInvalid(!ok);
          onChange?.(ok ? parsed.format('E.164') : null);
          return;
        }
      } catch {
        // unparseable — fall through to digit-only path
      }
    }
    const raw = rawText.replace(/[^\d]/g, '');
    setNationalNumber(raw);
    propagate(country, raw);
  };

  const currentCountry = allCountries.find(c => c.iso === country) || {
    iso: country, name: country, dialCode,
  };

  // Style tokens by variant. The "dark" variant matches the registration
  // form's other inputs (rounded-xl, dark navy translucent bg, light text,
  // gold focus ring) so the phone field doesn't visually break the row.
  const isDark = variant === 'dark';
  // 2026-06-29 — invalid state surfaces both the parent-provided
  // `invalid` prop AND our own internal "user typed garbage" flag.
  const showInvalid = invalid || internalInvalid;
  const wrapperCls = isDark
    ? `flex w-full items-stretch border bg-[#0A1128]/50 rounded-xl overflow-hidden box-border ${
        showInvalid ? 'border-red-500' : 'border-[#2D3748] focus-within:border-[#C9A24E]'
      }`
    : `flex w-full items-stretch border bg-white rounded-sm overflow-hidden box-border ${
        showInvalid ? 'border-red-500' : 'border-[#E2E4E0] focus-within:border-[#5C6B8C]'
      }`;
  const buttonCls = isDark
    ? 'flex flex-shrink-0 items-center gap-1.5 px-3 py-3 border-r border-[#2D3748] hover:bg-[#0A1128]/70 disabled:opacity-50'
    : 'flex flex-shrink-0 items-center gap-2 px-3 py-2 border-r border-[#E2E4E0] hover:bg-[#F5F5F0] disabled:opacity-50';
  const dialCls = isDark
    ? 'text-sm font-medium text-white'
    : 'text-sm font-medium text-[#0A1128]';
  const chevronCls = isDark ? 'text-[#A0AABF]' : 'text-[#5C6B8C]';
  const inputCls = isDark
    ? 'flex-1 min-w-0 px-4 py-3 outline-none bg-transparent text-sm text-white placeholder:text-[#A0AABF]'
    : 'flex-1 min-w-0 px-3 py-2 outline-none bg-white text-sm text-[#0A1128] placeholder:text-[#A0AABF]';
  const inputStyle = isDark
    ? {
        color: '#FFFFFF',
        WebkitTextFillColor: '#FFFFFF',
        caretColor: '#C9A24E',
        colorScheme: 'dark',
        fontSize: '16px',
        textAlign: 'left',
        direction: 'ltr',
        backgroundColor: 'transparent',
      }
    : {
        color: '#0A1128',
        WebkitTextFillColor: '#0A1128',
        caretColor: '#0A1128',
        colorScheme: 'light',
        fontSize: '16px',
        textAlign: 'left',
        direction: 'ltr',
        backgroundColor: '#FFFFFF',
      };

  return (
    <div className="relative w-full" ref={wrapperRef} data-testid={testId}>
      <div className={wrapperCls}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(o => !o)}
          className={buttonCls}
          data-testid={`${testId}-country-btn`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="text-lg leading-none">{flagEmoji(currentCountry.iso)}</span>
          <span className={dialCls}>{currentCountry.dialCode}</span>
          <ChevronDown size={14} className={chevronCls} />
        </button>
        <input
          type="tel"
          value={nationalNumber}
          onChange={handleInput}
          disabled={disabled}
          placeholder={placeholder}
          className={inputCls}
          data-testid={`${testId}-number`}
          inputMode="numeric"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          name={`phone-${testId}-${autofillBreaker}`}
          dir="ltr"
          style={inputStyle}
        />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-[min(320px,calc(100vw-2.5rem))] max-h-72 overflow-hidden border border-[#E2E4E0] rounded-sm bg-white shadow-lg"
             data-testid={`${testId}-country-list`}>
          <div className="flex items-center gap-2 p-2 border-b border-[#E2E4E0] bg-[#F5F5F0]">
            <Search size={14} className="text-[#5C6B8C]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country or code"
              className="flex-1 px-2 py-1 text-sm bg-white border border-[#E2E4E0] rounded-sm outline-none"
              data-testid={`${testId}-country-search`}
              autoFocus
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-[#5C6B8C] hover:text-[#0A1128]"><X size={14}/></button>
            )}
          </div>
          <div className="overflow-y-auto max-h-56">
            {filteredCountries.map((c) => (
              <button
                key={c.iso}
                type="button"
                onClick={() => handleSelect(c.iso)}
                className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F5F5F0] text-left text-sm ${c.iso === country ? 'bg-[#F5F5F0]' : ''}`}
                data-testid={`${testId}-country-${c.iso}`}
              >
                <span className="text-lg leading-none">{flagEmoji(c.iso)}</span>
                <span className="flex-1 text-[#0A1128]">{c.name}</span>
                <span className="text-[#5C6B8C]">{c.dialCode}</span>
              </button>
            ))}
            {filteredCountries.length === 0 && (
              <p className="text-center text-sm text-[#5C6B8C] py-4">No matching country</p>
            )}
          </div>
        </div>
      )}

      {/* 2026-06-29 — Inline validity hint. Renders ONLY when the
          user has typed something but it doesn't parse to a valid
          number. Prevents the silent-failure registration regression
          where a malformed phone became `null` without any cue. */}
      {internalInvalid && (
        <p
          className="mt-1 text-xs text-red-600"
          data-testid={`${testId}-error`}
          role="alert"
        >
          رقم الهاتف غير صالح. تأكد من الرمز الدولي وعدد الأرقام.
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
